import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Activity, ShieldCheck, Clock, AlertTriangle,
  CheckCircle2, History, Save, Send, Mail,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import FeatureAccessGate from "@/components/billing/FeatureAccessGate";
import { useAuth } from "@/context/AuthContext";
import { computeMedicalHistoryAlerts } from "@/lib/medicalHistoryAlerts";
import SendMedicalHistoryDialog from "./SendMedicalHistoryDialog";

// =====================================================================
// MedicalHistoryTab — single-screen form per patient.
//
// Lifecycle:
//   1. No row exists yet           → "Start medical history" CTA
//   2. Not Submitted               → editable form + "Submit" button
//   3. Submitted                   → read-only summary + "Verify" (admin/doctor)
//   4. Verified, not expired       → green banner + read-only summary
//   5. Verified, expired           → red banner + "Start a new one" CTA
//
// Data field schema (jsonb, evolvable — never read fields that don't
// exist; always default-coalesce):
//   {
//     allergies:        { none, drug:{yes,specify}, latex, food:{yes,specify}, other },
//     meds:             { none, blood_thinners:{yes,specify}, ... },
//     medications:      string,
//     conditions:       { <flag>: bool, ..., other: string },
//     surgeries:        string,
//     pregnancy:        { applicable: bool, pregnant: bool, weeks: string },
//     smoking:          "never" | "former" | "current",
//     alcohol:          "never" | "occasional" | "frequent",
//     last_physician_visit: "YYYY-MM-DD",
//     bleeding_notes:   string,
//     general_notes:    string,
//   }
// =====================================================================

// Conditions list — kept short and dentally relevant. The list is
// curated, not exhaustive; the "other" free-text box catches the
// long tail. Diagnosed-high-BP is captured here (history, not a
// per-visit measurement — those belong on the Visits tab).
const CONDITION_KEYS = [
  ["diabetes",          "Diabetes"],
  ["hypertension",      "High blood pressure (diagnosed)"],
  ["heart_disease",     "Heart disease"],
  ["bleeding_disorder", "Bleeding disorder"],
  ["asthma",            "Asthma"],
  ["epilepsy",          "Epilepsy / seizures"],
  ["kidney_disease",    "Kidney disease"],
  ["liver_disease",     "Liver disease / hepatitis"],
  ["thyroid_disorder",  "Thyroid disorder"],
  ["cancer",            "Cancer (current or past)"],
  ["tuberculosis",      "Tuberculosis"],
  ["hiv",               "HIV / AIDS"],
  ["covid_recent",      "COVID-19 in last 30 days"],
  ["acid_reflux",       "Acid reflux / GERD"],          // erosion risk
  ["eating_disorder",   "Eating disorder"],              // erosion risk
  ["dry_mouth",         "Dry mouth (xerostomia)"],       // caries risk
  ["dental_anxiety",    "Severe dental anxiety"],        // sedation planning
];

export const EMPTY_DATA = {
  // SAFETY-FIRST. Sequence mirrors the dentist's pre-procedure
  // decision tree: allergies → bleeding-risk meds → premedication →
  // conditions → pregnancy / surgeries → special care → broader context.
  // Vitals (BP / pulse) live on the Visits tab — measured at check-in,
  // not self-reported on intake.

  allergies: {
    none: false,
    drug:  { yes: false, specify: "" },
    latex: false,
    food:  { yes: false, specify: "" },
    anesthetic: { yes: false, specify: "" },  // local anesthetic reaction history
    other: "",
  },

  // Critical-meds flags broken out for clinical visibility.
  meds: {
    none: false,
    blood_thinners: { yes: false, specify: "" },   // warfarin, DOACs, aspirin, heparin
    bisphosphonates: { yes: false, specify: "" },  // ONJ risk on extractions
    steroids_long_term: { yes: false, specify: "" },
    chemo_radiation: { yes: false, specify: "" },  // current or past head/neck
    other: "",                                     // free-text catch-all
  },
  medications: "",

  // Cardiac history & antibiotic prophylaxis triggers.
  cardiac: {
    heart_attack:        { yes: false, when: "" },
    stroke:              { yes: false, when: "" },
    pacemaker:           false,
    artificial_valve:    false,
    joint_replacement:   { yes: false, specify: "" },
    rheumatic_fever:     false,
    premed_required:     false,
    premed_advised_by:   "",   // physician name / clinic
  },

  conditions:  { other: "" },
  surgeries:   "",

  pregnancy: { applicable: false, pregnant: false, weeks: "" },

  // Special care / accessibility context for the operatory.
  special_care: {
    developmental:    false,
    cognitive:        false,
    mobility:         false,
    caregiver_consent: false,
    caregiver_name:   "",
    communication:    "",     // free-text: sign language / interpreter / etc.
    notes:            "",
  },

  smoking:     "",
  alcohol:     "",
  last_physician_visit: "",
  bleeding_notes: "",
  general_notes:  "",
};

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function MedicalHistoryTabBody({ patientId, patient, visitVitals, demoHistory = null }) {
  const { clinicRole, activeClinic } = useAuth();
  const canVerify = !demoHistory && ["clinic_admin", "doctor"].includes(clinicRole);

  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState(null);
  const [history,   setHistory]   = useState(null);   // { id, status, data, expires_at, ... }
  const [form,      setForm]      = useState(EMPTY_DATA);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [sendOpen,     setSendOpen]     = useState(false);

  const load = useCallback(async () => {
    if (demoHistory) {
      const row = demoHistory.history ?? demoHistory;
      setLoading(false);
      setError(null);
      setHistory(row ?? null);
      setForm(row?.data ? mergeWithDefault(row.data) : EMPTY_DATA);
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("get_medical_history", { p_patient_id: patientId });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load medical history."); return; }
    setHistory(data ?? null);
    if (data?.data) setForm(mergeWithDefault(data.data));
    else            setForm(EMPTY_DATA);
  }, [patientId, demoHistory]);
  useEffect(() => { load(); }, [load]);

  async function handleStart() {
    setBusy(true);
    const { data, error: e } = await supabase.rpc("start_medical_history", { p_patient_id: patientId });
    setBusy(false);
    if (e) { toast.error(e.message ?? "Could not start medical history."); return; }
    setHistory(data);
    setForm(EMPTY_DATA);
    toast.success("Medical history started.");
  }

  async function handleSubmit() {
    if (!history?.id) return;
    setBusy(true);
    const { data, error: e } = await supabase.rpc("submit_medical_history", {
      p_history_id: history.id, p_data: form,
    });
    setBusy(false);
    if (e) { toast.error(e.message ?? "Could not submit."); return; }
    toast.success("Medical history submitted. Awaiting verification.");
    // submit_medical_history returns the row directly; pull a richer
    // shape via get_ to pick up verifier_name and is_current.
    await load();
  }

  async function handleVerify() {
    if (!history?.id) return;
    setBusy(true);
    const { error: e } = await supabase.rpc("verify_medical_history", {
      p_history_id: history.id,
    });
    setBusy(false);
    if (e) { toast.error(e.message ?? "Could not verify."); return; }
    toast.success("Medical history verified.");
    await load();
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="medical-history-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
        <p className="mt-3 text-sm text-slate-500">Loading medical history…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700" data-testid="medical-history-error">
        {error}
      </div>
    );
  }

  // ---- Empty state ---------------------------------------------------
  if (!history) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-10 text-center" data-testid="medical-history-empty">
        <div className="w-12 h-12 rounded-full bg-amber-50 mx-auto flex items-center justify-center">
          <Activity className="w-5 h-5 text-amber-600" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">No medical history on file</h3>
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
          Capture allergies, medications, and conditions before treatment. Required before proposing a treatment plan.
        </p>
        <button
          type="button"
          onClick={handleStart}
          disabled={busy}
          data-testid="medical-history-start"
          className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          Start medical history
        </button>
      </div>
    );
  }

  const editable = history.status === "Not Submitted";

  return (
    <div className="space-y-4" data-testid="medical-history-tab">
      <StatusBanner history={history} canVerify={canVerify} onVerify={handleVerify} onStart={handleStart} busy={busy} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setVersionsOpen(true)}
          className="text-xs font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          data-testid="medical-history-versions-open"
        >
          <History className="w-3.5 h-3.5" /> View past versions
        </button>
        {editable ? (
          <button
            type="button"
            onClick={() => setSendOpen(true)}
            disabled={busy}
            data-testid="medical-history-send-open"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-amber-300 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-40"
          >
            <Mail className="w-3.5 h-3.5" /> Send form to patient
          </button>
        ) : null}
      </div>

      {/* Patient context strip — age + DOB so dentists can calibrate
          dose / approach (pediatric vs geriatric vs adult) without
          flipping back to the patient header. */}
      <PatientContextStrip patient={patient} />

      {/* Auto-computed alerts — only the bits that change today's
          clinical decisions. Empty when the patient is unremarkable. */}
      <AlertsPanel form={form} history={history} patient={patient} visitVitals={visitVitals} />

      <MedicalHistoryFormFields form={form} setForm={setForm} editable={editable} />

      {/* The submit button stays inside this component since it's
          coupled to the parent's submit handler + busy state. */}
      {editable ? (
        <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="medical-history-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit medical history
          </button>
        </div>
      ) : null}

      {versionsOpen ? (
        <VersionsDialog patientId={patientId} onClose={() => setVersionsOpen(false)} />
      ) : null}

      <SendMedicalHistoryDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        historyId={history?.id}
        token={history?.submit_token}
        tokenExpiresAt={history?.token_expires_at}
        patientFirstName={patient?.first_name}
        patientEmail={patient?.email}
        clinicName={activeClinic?.clinic_name || activeClinic?.name}
        onLinkSent={() => load()}
      />
    </div>
  );
}

// =====================================================================
// MedicalHistoryFormFields — the actual input sections, factored out
// so /medical-history/:token (the public, unauthenticated submit page)
// can reuse the exact same fields.
// =====================================================================
export function MedicalHistoryFormFields({ form, setForm, editable }) {
  const setF = setForm;
  const allergiesNone = !!form.allergies?.none;
  const medsNone = !!form.meds?.none;
  const clearAllergiesForNone = (none) => ({
    none,
    drug: { yes: false, specify: "" },
    latex: false,
    food: { yes: false, specify: "" },
    anesthetic: { yes: false, specify: "" },
    other: "",
  });
  const clearMedsForNone = (none) => ({
    none,
    blood_thinners: { yes: false, specify: "" },
    bisphosphonates: { yes: false, specify: "" },
    steroids_long_term: { yes: false, specify: "" },
    chemo_radiation: { yes: false, specify: "" },
    other: "",
  });
  return (
    <>
      <div className="hidden" data-medical-history-form-fields-start /> {/* anchor for tests */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6" data-testid="medical-history-form">
        {/* ---------- 1. ALLERGIES — first because misreading allergies
                       can kill someone in the chair. ---------- */}
        <Section title="Allergies" tip="Tick everything that applies. Add specifics in the 'specify' box.">
          <CheckRow label="None / no known allergies"
            checked={allergiesNone}
            onChange={(v) => setF((f) => ({ ...f, allergies: v ? clearAllergiesForNone(true) : { ...f.allergies, none: false } }))}
            disabled={!editable} />
          <CheckRowWithSpecify
            label="Medication / drug allergy"
            checked={!!form.allergies.drug?.yes}
            specify={form.allergies.drug?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, drug: { ...f.allergies.drug, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, drug: { ...f.allergies.drug, specify: v } } }))}
            disabled={!editable || allergiesNone}
          />
          <CheckRowWithSpecify
            label="Local anesthetic reaction"
            checked={!!form.allergies.anesthetic?.yes}
            specify={form.allergies.anesthetic?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, anesthetic: { ...f.allergies.anesthetic, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, anesthetic: { ...f.allergies.anesthetic, specify: v } } }))}
            disabled={!editable || allergiesNone}
          />
          <CheckRow label="Latex allergy"
            checked={!!form.allergies.latex}
            onChange={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, latex: v } }))}
            disabled={!editable || allergiesNone} />
          <CheckRowWithSpecify
            label="Food allergy"
            checked={!!form.allergies.food?.yes}
            specify={form.allergies.food?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, food: { ...f.allergies.food, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, food: { ...f.allergies.food, specify: v } } }))}
            disabled={!editable || allergiesNone}
          />
          <FieldText
            id="allergies-other" label="Other allergies"
            value={form.allergies.other || ""}
            onChange={(v) => setF((f) => ({ ...f, allergies: { ...f.allergies, none: false, other: v } }))}
            placeholder="e.g. pollen, dust, animal hair"
            disabled={!editable || allergiesNone}
          />
        </Section>

        {/* ---------- 3. HIGH-RISK MEDS — flag bleeding / ONJ / immune
                       suppression risks separately so they don't get
                       lost in a free-text medications blob. ---------- */}
        <Section title="High-risk medications" tip="Flag anything that changes our procedure protocol or extraction risk.">
          <CheckRow label="Currently taking no medications"
            checked={medsNone}
            onChange={(v) => setF((f) => ({ ...f, meds: v ? clearMedsForNone(true) : { ...f.meds, none: false }, medications: v ? "" : f.medications }))}
            disabled={!editable} />
          <CheckRowWithSpecify
            label="Blood thinners / anticoagulants"
            checked={!!form.meds?.blood_thinners?.yes}
            specify={form.meds?.blood_thinners?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, blood_thinners: { ...f.meds?.blood_thinners, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, blood_thinners: { ...f.meds?.blood_thinners, specify: v } } }))}
            specifyPlaceholder="e.g. Warfarin 5mg / Aspirin 81mg / Apixaban"
            disabled={!editable || medsNone}
          />
          <CheckRowWithSpecify
            label="Bisphosphonates (oral or IV)"
            checked={!!form.meds?.bisphosphonates?.yes}
            specify={form.meds?.bisphosphonates?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, bisphosphonates: { ...f.meds?.bisphosphonates, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, bisphosphonates: { ...f.meds?.bisphosphonates, specify: v } } }))}
            specifyPlaceholder="e.g. Alendronate 70mg weekly · since 2022"
            disabled={!editable || medsNone}
          />
          <CheckRowWithSpecify
            label="Long-term steroids"
            checked={!!form.meds?.steroids_long_term?.yes}
            specify={form.meds?.steroids_long_term?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, steroids_long_term: { ...f.meds?.steroids_long_term, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, steroids_long_term: { ...f.meds?.steroids_long_term, specify: v } } }))}
            specifyPlaceholder="e.g. Prednisone 10mg daily"
            disabled={!editable || medsNone}
          />
          <CheckRowWithSpecify
            label="Chemotherapy / radiation (current or past head & neck)"
            checked={!!form.meds?.chemo_radiation?.yes}
            specify={form.meds?.chemo_radiation?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, chemo_radiation: { ...f.meds?.chemo_radiation, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, meds: { ...f.meds, none: false, chemo_radiation: { ...f.meds?.chemo_radiation, specify: v } } }))}
            specifyPlaceholder="e.g. Head & neck radiation 2020"
            disabled={!editable || medsNone}
          />
          <FieldTextarea
            id="medications" label="Other current medications, supplements, herbals" rows={3}
            value={form.medications}
            onChange={(v) => setF((f) => ({ ...f, medications: v, meds: { ...f.meds, none: false } }))}
            placeholder="One per line, with dosage if known. 'None' if nothing."
            disabled={!editable || medsNone}
          />
        </Section>

        {/* ---------- 4. CARDIAC & PREMEDICATION — separate because
                       these trigger antibiotic prophylaxis decisions. */}
        <Section title="Cardiac history & premedication" tip="Pacemaker, valve, joint replacement, and rheumatic fever may require antibiotic prophylaxis.">
          <CheckRowWithSpecify
            label="Past heart attack"
            checked={!!form.cardiac?.heart_attack?.yes}
            specify={form.cardiac?.heart_attack?.when || ""}
            onCheck={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, heart_attack: { ...f.cardiac?.heart_attack, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, heart_attack: { ...f.cardiac?.heart_attack, when: v } } }))}
            specifyPlaceholder="When? e.g. 2021"
            disabled={!editable}
          />
          <CheckRowWithSpecify
            label="Past stroke"
            checked={!!form.cardiac?.stroke?.yes}
            specify={form.cardiac?.stroke?.when || ""}
            onCheck={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, stroke: { ...f.cardiac?.stroke, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, stroke: { ...f.cardiac?.stroke, when: v } } }))}
            specifyPlaceholder="When? e.g. 2023"
            disabled={!editable}
          />
          <CheckRow label="Pacemaker"
            checked={!!form.cardiac?.pacemaker}
            onChange={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, pacemaker: v } }))}
            disabled={!editable} />
          <CheckRow label="Artificial heart valve"
            checked={!!form.cardiac?.artificial_valve}
            onChange={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, artificial_valve: v } }))}
            disabled={!editable} />
          <CheckRowWithSpecify
            label="Joint replacement"
            checked={!!form.cardiac?.joint_replacement?.yes}
            specify={form.cardiac?.joint_replacement?.specify || ""}
            onCheck={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, joint_replacement: { ...f.cardiac?.joint_replacement, yes: v } } }))}
            onSpecify={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, joint_replacement: { ...f.cardiac?.joint_replacement, specify: v } } }))}
            specifyPlaceholder="e.g. Right hip 2019"
            disabled={!editable}
          />
          <CheckRow label="Rheumatic fever history"
            checked={!!form.cardiac?.rheumatic_fever}
            onChange={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, rheumatic_fever: v } }))}
            disabled={!editable} />
          <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
            <CheckRow label="Antibiotic prophylaxis required before procedures"
              checked={!!form.cardiac?.premed_required}
              onChange={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, premed_required: v } }))}
              disabled={!editable} />
            {form.cardiac?.premed_required ? (
              <FieldText
                id="premed-advised-by" label="Advised by"
                value={form.cardiac?.premed_advised_by || ""}
                onChange={(v) => setF((f) => ({ ...f, cardiac: { ...f.cardiac, premed_advised_by: v } }))}
                placeholder="e.g. Dr. Reyes, Cardiologist"
                disabled={!editable}
              />
            ) : null}
          </div>
        </Section>

        {/* ---------- 5. CONDITIONS ---------- */}
        <Section title="Medical conditions" tip="Tick anything the patient has been diagnosed with. Chairside BP is still measured at check-in.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CONDITION_KEYS.map(([key, label]) => (
              <CheckRow key={key}
                label={label}
                checked={!!form.conditions?.[key]}
                onChange={(v) => setF((f) => ({ ...f, conditions: { ...f.conditions, [key]: v } }))}
                disabled={!editable}
              />
            ))}
          </div>
          <FieldText
            id="conditions-other" label="Other"
            value={form.conditions?.other || ""}
            onChange={(v) => setF((f) => ({ ...f, conditions: { ...f.conditions, other: v } }))}
            placeholder="Any other diagnoses we should know about"
            disabled={!editable}
          />
        </Section>

        {/* ---------- 5. PREGNANCY ---------- */}
        <Section title="Pregnancy">
          <CheckRow
            label="Patient may be pregnant or planning to be"
            checked={!!form.pregnancy?.applicable}
            onChange={(v) => setF((f) => ({ ...f, pregnancy: { ...f.pregnancy, applicable: v } }))}
            disabled={!editable}
          />
          {form.pregnancy?.applicable ? (
            <div className="pl-6 space-y-2">
              <CheckRow
                label="Currently pregnant"
                checked={!!form.pregnancy?.pregnant}
                onChange={(v) => setF((f) => ({ ...f, pregnancy: { ...f.pregnancy, pregnant: v } }))}
                disabled={!editable}
              />
              {form.pregnancy?.pregnant ? (
                <FieldText
                  id="pregnancy-weeks" label="Weeks (approx)"
                  value={form.pregnancy?.weeks || ""}
                  onChange={(v) => setF((f) => ({ ...f, pregnancy: { ...f.pregnancy, weeks: v } }))}
                  placeholder="e.g. 14"
                  maxLength={6}
                  disabled={!editable}
                />
              ) : null}
            </div>
          ) : null}
        </Section>

        {/* ---------- 6. RECENT HOSPITALIZATIONS / SURGERIES ---------- */}
        <Section title="Recent hospitalizations or surgeries">
          <FieldTextarea
            id="surgeries" rows={2}
            value={form.surgeries}
            onChange={(v) => setF((f) => ({ ...f, surgeries: v }))}
            placeholder="e.g. Appendectomy, Mar 2024"
            disabled={!editable}
          />
        </Section>

        {/* ---------- 7. SPECIAL CARE — accessibility + caregiver ---------- */}
        <Section title="Special care" tip="So the team can prepare the operatory and the consent flow.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <CheckRow label="Developmental disability"
              checked={!!form.special_care?.developmental}
              onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, developmental: v } }))}
              disabled={!editable} />
            <CheckRow label="Cognitive impairment / dementia"
              checked={!!form.special_care?.cognitive}
              onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, cognitive: v } }))}
              disabled={!editable} />
            <CheckRow label="Mobility / wheelchair"
              checked={!!form.special_care?.mobility}
              onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, mobility: v } }))}
              disabled={!editable} />
            <CheckRow label="Caregiver consents on patient's behalf"
              checked={!!form.special_care?.caregiver_consent}
              onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, caregiver_consent: v } }))}
              disabled={!editable} />
          </div>
          {form.special_care?.caregiver_consent ? (
            <FieldText
              id="caregiver-name" label="Caregiver name + relationship"
              value={form.special_care?.caregiver_name || ""}
              onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, caregiver_name: v } }))}
              placeholder="e.g. Maria Santos, daughter"
              disabled={!editable}
            />
          ) : null}
          <FieldText
            id="communication" label="Communication preferences"
            value={form.special_care?.communication || ""}
            onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, communication: v } }))}
            placeholder="e.g. Sign language, Tagalog only, hearing-impaired"
            disabled={!editable}
          />
          <FieldTextarea
            id="special-care-notes" label="Other special care notes" rows={2}
            value={form.special_care?.notes || ""}
            onChange={(v) => setF((f) => ({ ...f, special_care: { ...f.special_care, notes: v } }))}
            placeholder="Anything the team should prepare for"
            disabled={!editable}
          />
        </Section>

        <Section title="Lifestyle">
          <RadioRow id="smoking" label="Smoking"
            value={form.smoking}
            options={[["never", "Never"], ["former", "Former"], ["current", "Current"]]}
            onChange={(v) => setF((f) => ({ ...f, smoking: v }))}
            disabled={!editable}
          />
          <RadioRow id="alcohol" label="Alcohol"
            value={form.alcohol}
            options={[["never", "Never"], ["occasional", "Occasional"], ["frequent", "Frequent"]]}
            onChange={(v) => setF((f) => ({ ...f, alcohol: v }))}
            disabled={!editable}
          />
        </Section>

        <Section title="Other">
          <FieldText id="last-physician-visit" label="Last physician visit" type="date"
            value={form.last_physician_visit}
            onChange={(v) => setF((f) => ({ ...f, last_physician_visit: v }))}
            disabled={!editable}
          />
          <FieldTextarea
            id="bleeding-notes" label="Notes on bleeding tendency" rows={2}
            value={form.bleeding_notes}
            onChange={(v) => setF((f) => ({ ...f, bleeding_notes: v }))}
            placeholder="Heavy bleeding after extractions? Easy bruising?"
            disabled={!editable}
          />
          <FieldTextarea
            id="general-notes" label="General notes" rows={3}
            value={form.general_notes}
            onChange={(v) => setF((f) => ({ ...f, general_notes: v }))}
            placeholder="Anything else the dentist should know."
            disabled={!editable}
          />
        </Section>
      </div>
    </>
  );
}

// ---- StatusBanner ----------------------------------------------------
function StatusBanner({ history, canVerify, onVerify, onStart, busy }) {
  const status = history?.status;

  if (status === "Verified") {
    const expired = history?.is_current === false;
    if (expired) {
      return (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="medical-history-banner-expired">
          <AlertTriangle className="w-5 h-5 shrink-0 text-rose-500 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-900">This medical history has expired.</p>
            <p className="text-xs text-rose-700 mt-0.5">
              Expired {fmtDate(history.expires_at)}. Capture a fresh one before proceeding with treatment.
            </p>
          </div>
          <button
            type="button" onClick={onStart} disabled={busy}
            data-testid="medical-history-renew"
            className="px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-40"
          >
            Start new history
          </button>
        </div>
      );
    }
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="medical-history-banner-verified">
        <ShieldCheck className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-900">
            Verified {history.verifier_name ? `by ${history.verifier_name}` : ""} on {fmtDate(history.verified_at)}.
          </p>
          <p className="text-xs text-emerald-700 mt-0.5">
            Valid until {fmtDate(history.expires_at)}.
          </p>
        </div>
      </div>
    );
  }

  if (status === "Submitted") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="medical-history-banner-submitted">
        <Clock className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">
            Submitted {fmtDate(history.submitted_at)} — awaiting verification.
          </p>
          {!canVerify ? (
            <p className="text-xs text-amber-700 mt-0.5">A doctor or clinic admin can verify.</p>
          ) : null}
        </div>
        {canVerify ? (
          <button
            type="button" onClick={onVerify} disabled={busy}
            data-testid="medical-history-verify"
            className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Verify
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid="medical-history-banner-draft">
      <Save className="w-5 h-5 shrink-0 text-slate-500 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">Draft — not yet submitted.</p>
        <p className="text-xs text-slate-600 mt-0.5">Fill in the fields below and click <span className="font-medium">Submit</span>.</p>
      </div>
    </div>
  );
}

// ---- Past versions modal --------------------------------------------
function VersionsDialog({ patientId, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    supabase.rpc("list_medical_history_versions", { p_patient_id: patientId })
      .then(({ data, error: e }) => {
        if (e) setError(e.message ?? "Could not load versions.");
        else   setRows(data ?? []);
      });
  }, [patientId]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="medical-history-versions-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900 inline-flex items-center gap-2">
            <History className="w-4 h-4" /> Medical history versions
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">Close</button>
        </div>
        <div className="p-5 overflow-y-auto">
          {error ? (
            <p className="text-sm text-rose-700">{error}</p>
          ) : rows == null ? (
            <p className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No versions recorded yet.</p>
          ) : (
            <ol className="space-y-3">
              {rows.map((r) => (
                <li key={r.id} className="flex items-start gap-3" data-testid={`medical-history-version-${r.id}`}>
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{r.event}</p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(r.occurred_at, true)}{r.actor_name ? ` · by ${r.actor_name}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- PatientContextStrip ---------------------------------------------
// Age + DOB up top so the dentist can calibrate (pediatric / adult /
// geriatric). Quietly hides if no DOB is on file.
function PatientContextStrip({ patient }) {
  const age = ageFromDob(patient?.date_of_birth);
  if (!patient || (!age && !patient.date_of_birth)) return null;
  const dobStr = patient.date_of_birth
    ? new Date(patient.date_of_birth).toLocaleDateString(undefined, { dateStyle: "medium" })
    : null;
  let band = "Adult";
  if (age != null) {
    if (age < 13)      band = "Pediatric";
    else if (age < 18) band = "Adolescent";
    else if (age >= 65) band = "Geriatric";
  }
  return (
    <div
      className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4 flex-wrap"
      data-testid="medical-history-patient-context"
    >
      <div>
        <p className="text-[11px] font-bold tracking-wider uppercase text-slate-400">Patient context</p>
        <p className="text-sm text-slate-800 mt-0.5">
          {age != null ? <span className="font-semibold">Age {age}</span> : null}
          {age != null && dobStr ? <span className="text-slate-400 mx-2">·</span> : null}
          {dobStr ? <span>DOB {dobStr}</span> : null}
          <span className="ml-2 inline-flex items-center text-[10px] font-semibold tracking-wider uppercase rounded px-1.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-200">
            {band}
          </span>
        </p>
      </div>
    </div>
  );
}

// ---- AlertsPanel -----------------------------------------------------
// Auto-computed red-flag summary. The dentist's 5-second pre-procedure
// scan should consult ONLY this panel for "anything that changes my
// plan today?". Empty state shows a quiet green confirmation.
function AlertsPanel({ form, history, patient, visitVitals }) {
  const alerts = computeMedicalHistoryAlerts(form, { history, patient, visitVitals });
  const blockingAlerts = alerts.filter((a) => a.severity === "blocking");
  const advisoryAlerts = alerts.filter((a) => a.severity !== "blocking");
  if (alerts.length === 0) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2.5"
        data-testid="medical-history-alerts-empty"
      >
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
        <p className="text-sm text-emerald-900">
          <span className="font-semibold">No red flags.</span>
          <span className="ml-1 text-emerald-800/80">No allergies, anticoagulants, premed needs, or other alerts on file.</span>
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="medical-history-alerts">
      {blockingAlerts.length > 0 ? (
        <div
          className="bg-red-100 border-2 border-red-400 rounded-xl p-3"
          data-testid="medical-history-alerts-blocking"
          role="alert"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-700" />
            <p className="text-sm font-bold text-red-950">
              Blocking medical alert
            </p>
          </div>
          <ul className="space-y-1 ml-6 list-disc text-sm text-red-950">
            {blockingAlerts.map((a, i) => (
              <li key={`blocking-${i}`} data-testid={`medical-history-alert-blocking-${i}`}>
                <span className="font-semibold">{a.label}:</span>{" "}
                <span>{a.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {advisoryAlerts.length > 0 ? (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="medical-history-alerts-advisory">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            <p className="text-sm font-semibold text-rose-900">
              {advisoryAlerts.length} {advisoryAlerts.length === 1 ? "alert" : "alerts"} for today's clinical decisions
            </p>
          </div>
          <ul className="space-y-1 ml-6 list-disc text-sm text-rose-900">
            {advisoryAlerts.map((a, i) => (
              <li key={`advisory-${i}`} data-testid={`medical-history-alert-${i}`}>
                <span className="font-semibold">{a.label}:</span>{" "}
                <span className="opacity-90">{a.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ---- Tiny field helpers ----------------------------------------------
function Section({ title, tip, children }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {tip ? <p className="text-[11px] text-slate-400 mt-0.5">{tip}</p> : null}
      <div className="mt-3 space-y-2">{children}</div>
    </section>
  );
}

function CheckRow({ label, checked, onChange, disabled }) {
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? "text-slate-400 cursor-not-allowed" : "text-slate-700"}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-300 disabled:bg-slate-100 disabled:text-slate-300"
      />
      <span>{label}</span>
    </label>
  );
}

function CheckRowWithSpecify({ label, checked, specify, onCheck, onSpecify, disabled, specifyPlaceholder = "Specify" }) {
  return (
    <div>
      <CheckRow label={label} checked={checked} onChange={onCheck} disabled={disabled} />
      {checked ? (
        <input
          type="text"
          value={specify}
          onChange={(e) => onSpecify(e.target.value)}
          disabled={disabled}
          placeholder={specifyPlaceholder}
          className="ml-6 mt-1 px-2 py-1 text-xs border border-slate-200 rounded w-full max-w-sm focus:outline-none focus:border-amber-400 disabled:bg-slate-50 disabled:text-slate-500"
        />
      ) : null}
    </div>
  );
}

function FieldText({ id, label, value, onChange, placeholder, type = "text", maxLength, disabled }) {
  return (
    <div>
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      ) : null}
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        data-testid={`field-${id}`}
        className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-amber-400 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  );
}

function FieldTextarea({ id, label, value, onChange, placeholder, rows = 3, disabled }) {
  return (
    <div>
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      ) : null}
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`field-${id}`}
        className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded resize-y focus:outline-none focus:border-amber-400 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  );
}

function RadioRow({ id, label, value, options, onChange, disabled }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-xs font-medium text-slate-600 w-20">{label}</span>
      {options.map(([v, lbl]) => (
        <label key={v} className="inline-flex items-center gap-1 text-sm text-slate-700">
          <input
            type="radio"
            name={id}
            value={v}
            checked={value === v}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="w-4 h-4 text-amber-500 focus:ring-amber-300"
          />
          {lbl}
        </label>
      ))}
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------
export function mergeWithDefault(d) {
  // Defensive merge: server may have a sparse `data` if older form
  // versions were submitted. Fill missing keys from the empty default
  // so the form always has the shape the inputs expect.
  return {
    ...EMPTY_DATA,
    ...d,
    allergies: { ...EMPTY_DATA.allergies, ...(d.allergies || {}),
      drug:       { ...EMPTY_DATA.allergies.drug,       ...(d.allergies?.drug       || {}) },
      food:       { ...EMPTY_DATA.allergies.food,       ...(d.allergies?.food       || {}) },
      anesthetic: { ...EMPTY_DATA.allergies.anesthetic, ...(d.allergies?.anesthetic || {}) },
    },
    meds: { ...EMPTY_DATA.meds, ...(d.meds || {}),
      blood_thinners:     { ...EMPTY_DATA.meds.blood_thinners,     ...(d.meds?.blood_thinners     || {}) },
      bisphosphonates:    { ...EMPTY_DATA.meds.bisphosphonates,    ...(d.meds?.bisphosphonates    || {}) },
      steroids_long_term: { ...EMPTY_DATA.meds.steroids_long_term, ...(d.meds?.steroids_long_term || {}) },
      chemo_radiation:    { ...EMPTY_DATA.meds.chemo_radiation,    ...(d.meds?.chemo_radiation    || {}) },
    },
    cardiac: { ...EMPTY_DATA.cardiac, ...(d.cardiac || {}),
      heart_attack:      { ...EMPTY_DATA.cardiac.heart_attack,      ...(d.cardiac?.heart_attack      || {}) },
      stroke:            { ...EMPTY_DATA.cardiac.stroke,            ...(d.cardiac?.stroke            || {}) },
      joint_replacement: { ...EMPTY_DATA.cardiac.joint_replacement, ...(d.cardiac?.joint_replacement || {}) },
    },
    conditions:   { ...EMPTY_DATA.conditions,   ...(d.conditions   || {}) },
    pregnancy:    { ...EMPTY_DATA.pregnancy,    ...(d.pregnancy    || {}) },
    special_care: { ...EMPTY_DATA.special_care, ...(d.special_care || {}) },
  };
}

function fmtDate(ts, withTime = false) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined,
      withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" });
  } catch { return ts; }
}

export default function MedicalHistoryTab(props) {
  return (
    <FeatureAccessGate featureKey="medical_history">
      <MedicalHistoryTabBody {...props} />
    </FeatureAccessGate>
  );
}
